<template>
  <main class="loyalty-system-settings">
    <div class="loyalty-system-settings__body">
      <section class="loyalty-system-settings__head">
        <h1>Настройки программы лояльности</h1>
      </section>
      <template v-if="organizationLoyaltySettings">
        <form v-on:submit.prevent="onSubmitOrganizationLoyaltySystem">
          <section class="loyalty-system-settings__content">
            <p class="loyalty-system-settings__subtitle">
              Выберите одну систему, которая будет действовать для всех пациентов
            </p>
            <switch-loyalty-system-settings
              v-model="organizationLoyaltySettings.mode"
              v-bind:disabled="!bonusSettings?.isMainBranch"
              v-on:edit-bonus="editBonusSettings"
              v-on:edit-discount="editDiscountSettings"
              v-on:input="onLoyaltyModeChange"
            />
          </section>
        </form>
      </template>
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        v-if="isShowBonusSidebar"
        v-bind:need-confirm="needConfirmBonusSettings"
        label="Правила начисления и списания бонусов"
        v-on:cancel="closeBonusSidebar"
      >
        <form-bonus-system-settings
          v-if="bonusSettings"
          v-model="bonusSettings"
          v-bind:service-price-list="servicePriceList"
          v-bind:services="services"
          v-bind:violations="bonusViolations"
          v-on:submit="onSubmitBonusSettings"
          v-on:close="closeBonusSidebar"
        />
      </confirm-side-dialog>
    </side-slide-transition>
    <side-slide-transition>
      <confirm-side-dialog
        v-if="isShowDiscountSidebar"
        label="Уровни скидки"
        v-bind:need-confirm="needConfirmDiscountSettings"
        v-on:cancel="closeDiscountSidebar"
      >
        <form-discount-settings
          v-if="discountSettings"
          v-model="discountSettings"
          v-bind:violations="discountViolations"
          v-on:delete="addDeleteCandidate"
          v-on:close="closeDiscountSidebar"
          v-on:submit="onSubmitDiscountSettings"
        />
      </confirm-side-dialog>
    </side-slide-transition>
    <modal-dialog
      title="Удалить уровень скидки?"
      width="100%"
      max-width="400px"
      title-center
      with-cross
      v-model="isShowDeleteModal"
    >
      <confirm-modal
        confirm-title="Удалить"
        cancel-title="Отмена"
        v-on:on-confirm="onDelete"
        v-on:on-cancel="isShowDeleteModal = false"
      />
    </modal-dialog>
  </main>
</template>

<script>
import FormBonusSystemSettings from '@/components/FormBonusSystemSettings.vue';
import FormDiscountSettings from '@/components/FormDiscountSettings.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import SwitchLoyaltySystemSettings from '@/components/SwitchLoyaltySystemSettings.vue';
import notifierMixin from '@/mixins/notifierMixin';

export default {
  /**
   * @description Страница настройки системы лояльности
   */
  name: 'LoyaltySystemSettings',
  components: {
    ModalDialog,
    ConfirmModal,
    ConfirmSideDialog,
    SideSlideTransition,
    FormBonusSystemSettings,
    FormDiscountSettings,
    SwitchLoyaltySystemSettings,
  },
  computed: {
    /**
     * @description Проверить, были ли изменения в настройках системы лояльности организации
     * @returns {boolean}
     */
    isChange() {
      return this.organizationLoyaltySettings.isChanged();
    },
    /**
     * @description Проверить, были ли изменения в настройках системы скидок
     * @returns {boolean}
     */
    needConfirmDiscountSettings() {
      return this.discountSettings.isChanged();
    },
    /**
     * @description Проверить, были ли изменения в настройках системы бонусов
     * @returns {boolean}
     */
    needConfirmBonusSettings() {
      return this.bonusSettings.isChanged();
    },
  },
  created() {
    this.fetchOrganizationLoyaltySettings();
    this.fetchBonusSettings();
    this.fetchServicePriceList();
    this.fetchServicesForIncreasePriceList();
    this.fetchDiscountLevels();
  },
  data() {
    return {
      /**
       * @description Показывать сайдбар настройки бонусов
       * @type {boolean}
       */
      isShowBonusSidebar: false,
      /**
       * @description Показывать сайдбар настройки дисконтных карт
       * @type {boolean}
       */
      isShowDiscountSidebar: false,
      /**
       * @description Настройки бонусной системы
       * @type {BonusSettings|null}
       */
      bonusSettings: null,
      /**
       * @description Нарушения для бонусной формы
       * @type {Object}
       */
      bonusViolations: {},
      /**
       * @description Ошибки
       * @type {Object}
       */
      violations: {},
      /**
       * @description Список прайс-листов услуг
       * @type {Array<ServicePriceList>}
       */
      servicePriceList: [],
      /**
       * @description Список услуг для выбора
       * @type {Array}
       */
      services: [],
      /**
       * @description Настройки дисконтных карт
       * @type {DiscountSettings|null}
       */
      discountSettings: null,
      /**
       * @description Нарушения для дисконтной формы
       * @type {Object}
       */
      discountViolations: {},
      /**
       * @description Показывать модальное окно удаления
       * @type {boolean}
       */
      isShowDeleteModal: false,
      /**
       * @description Идентификатор кандидата на удаление
       * @type {number|null}
       */
      deleteCandidateId: null,
      /**
       * @description Настройки системы лояльности организации
       * @type {OrganizationLoyaltySystemSettings|null}
       */
      organizationLoyaltySettings: null,
    };
  },
  methods: {
    /**
     * @description Обработать изменение режима лояльности
     * @param {String} mode - Новый режим лояльности
     * @returns {void}
     */
    onLoyaltyModeChange(mode) {
      const request = {
        loyaltySystemMode: mode,
      };
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.fetchOrganizationLoyaltySettings();
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
        this.violations = error.allCauses();
      };
      this
        .$loyaltySystemGateway
        .storeOrganizationLoyaltySettings(request)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Обработать отправку формы настроек системы лояльности организации
     * @returns {void}
     */
    onSubmitOrganizationLoyaltySystem() {
      this.onLoyaltyModeChange(this.organizationLoyaltySettings.mode);
    },

    /**
     * @description Открыть сайдбар редактирования настроек бонусов
     * @returns {void}
     */
    editBonusSettings() {
      this.isShowBonusSidebar = true;
    },
    /**
     * @description Открыть сайдбар редактирования настроек дисконтных карт
     * @returns {void}
     */
    editDiscountSettings() {
      this.isShowDiscountSidebar = true;
    },
    /**
     * @description Закрыть сайдбар настройки бонусов
     * @returns {void}
     */
    closeBonusSidebar() {
      this.isShowBonusSidebar = false;
      this.bonusSettings = this.bonusSettings.toPrevious();
    },
    /**
     * @description Закрыть сайдбар настройки дисконтных карт
     * @returns {void}
     */
    closeDiscountSidebar() {
      this.isShowDiscountSidebar = false;
      this.discountSettings = this.discountSettings.toPrevious();
    },
    /**
     * @description Затянуть прайс-листы
     * @returns {void}
     */
    async fetchServicePriceList() {
      const result = await this.$serviceGateway.getServicePriceListsForView();
      const { prices } = result.fold({ categories: [] })((response) => response);
      this.servicePriceList = prices;
    },
    /**
     * @description Запросить список категорий услуг
     * @returns {Promise<void>}
     */
    async fetchServicesForIncreasePriceList() {
      const onSuccess = (list) => {
        this.services = list;
      };
      const onFail = (error) => { this.notifyFail(error.title); };
      this.$serviceGateway.allForView().then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Затянуть настройки бонусной системы клиники
     * @returns {void}
     */
    fetchBonusSettings() {
      const onSuccess = (settings) => {
        this.bonusSettings = settings;
      };
      const onFail = (leftResponse) => {
        this.notifyFail(leftResponse.getTitle());
      };
      this
        .$loyaltySystemGateway
        .getClinicBonusSettings()
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Затянуть настройки бонусной системы организации
     * @returns {void}
     */
    fetchOrganizationLoyaltySettings() {
      const onSuccess = (settings) => {
        this.organizationLoyaltySettings = settings;
      };
      const onFail = (leftResponse) => {
        this.notifyFail(leftResponse.getTitle());
      };
      this
        .$loyaltySystemGateway
        .viewOrganizationLoyaltySettings()
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Обработать событие отправки формы бонусов
     * @returns {void}
     */
    onSubmitBonusSettings() {
      this.storeBonusSettings();
    },
    /**
     * @description Сохранить настройки бонусной системы
     * @returns {void}
     */
    storeBonusSettings() {
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.bonusViolations = {};
        this.fetchBonusSettings();
        this.closeBonusSidebar();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.bonusViolations = error.allCauses();
      };
      this
        .$loyaltySystemGateway
        .storeBonusSettings(this.bonusSettings)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Затянуть все правила дисконтных карт
     * @returns {void}
     */
    fetchDiscountLevels() {
      const onSuccess = (response) => {
        this.discountSettings = response;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.$loyaltySystemGateway.getLevels()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать отправку формы дисконтных карт
     * @returns {void}
     */
    onSubmitDiscountSettings() {
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.discountViolations = {};
        this.fetchDiscountLevels();
        this.closeDiscountSidebar();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.discountViolations = error.allCauses();
      };
      this
        .$loyaltySystemGateway
        .storeLevels(this.discountSettings)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Добавить кандидата на удаление
     * @param {number} candidateId
     * @returns {void}
     */
    addDeleteCandidate(candidateId) {
      this.deleteCandidateId = candidateId;
      this.isShowDeleteModal = true;
    },
    /**
     * @description Обработать удаление
     * @returns {void}
     */
    onDelete() {
      const rule = this.discountSettings.getLevel(this.deleteCandidateId);
      if (rule.id === '') {
        this.discountSettings.deleteLevel(this.deleteCandidateId);
        this.isShowDeleteModal = false;
        return;
      }
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.isShowDeleteModal = false;
        this.discountViolations = {};
        this.fetchDiscountLevels();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.discountViolations = error.allCauses();
      };
      this
        .$loyaltySystemGateway
        .deleteLevel(this.deleteCandidateId)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
  mixins: [notifierMixin],
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.loyalty-system-settings {
  display: flex;
  width: 100%;
  height: 100%;
  &__body {
    width: 100%;
    padding: 38px 20px 48px 23px;
    overflow-y: scroll;
  }
  &__head {
    margin-bottom: 25px;
  }
  &__content {
    margin-bottom: 20px;
  }
  &__subtitle {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 20px !important;
    color: var(--reading-color);
  }
}
</style>
